import EventEmitter from "events";
import ConnectionManager from "./connectionManager";
import AuthSessionManager from "./authManager";
import MessageParser from "./messageParser";
import MultipartParser from "./multiPartParser";
import ControlMessageParser from "./cmsgParser";
import { NmsrpPacketManager } from "./messageBuilder";
import generateRandomString from "./utilities/generators";
import MsrpUri from "./msrpUri";
import Wrapper from "../../src/utils/msrpWrapper";
import { CmTypes, RequestJobs } from "./cmsgTypes";
import ChunkManager from "./chunkManager";
import { historyParser } from "./MessageHistoryParser";
import { toast } from "react-toastify";

class Endpoint extends EventEmitter {
  constructor(host, port, name, password,companyDomain, sessionId, authHost, authPort, authSessionId, role = "agent", subrole = "") {
    console.log("constructing end point:", {
      host: host,
      port: port,
      name: name,
      companyDomain: companyDomain,
      password: password,
      sessionId: sessionId,
      authHost: authHost,
      authPort: authPort,
      authSessionId: authSessionId,
      role: role,
      subrole: subrole,
    });
    super();
    this.host = host;
    this.port = port;
    this.username = name;
    this.password = password;
    this.companyDomain = companyDomain;
    this.authHost = authHost;
    this.authPort = authPort;
    this.authSessionId = sessionId;
    this.sessionId = sessionId;
    // MSRP URI role parameter: ALWAYS "agent". The frontline/backoffice
    // distinction is carried by the separate `subrole` parameter instead
    // (frontline → no subrole, backoffice/management/other → "backoffice").
    this.role = role;
    this.subrole = subrole;
    this.fromPath = new MsrpUri("", host, port, sessionId, "ws");
    this.toPath = new MsrpUri(name, authHost, authPort, "", "ws", role, companyDomain, subrole);

    // Store original authentication toPath - NEVER overwrite this
    // This is used for AUTH messages during initial login and reconnections
    this.authToPath = new MsrpUri(name, authHost, authPort, "", "ws", role, companyDomain, subrole);

    this.usePath = null;
    this.connectionManager = new ConnectionManager(this);
    this.connectionManager.setPacketParser(new MessageParser(this));
    this.authSessionManager = new AuthSessionManager(this);
    this.state = "DISCONNECTED";
    this.pendingMessages = {};
    this.messageTimers = {};
    this.manager = new NmsrpPacketManager();
    //this.wrapper = new Wrapper();
    this.chunkManager = null;
    this.isWaiting = false;

    this.pingInterval = null;
    this.authFailureCount = 0;
    this.MAX_AUTH_FAILURES = 3;
    this.hasEverConnected = false; // Track first connection vs reconnection

    // Listen for the all events
    this.connectionManager.on("connected", (protocol) => {
      console.log("in connected event");
      this.onConnectionEstablished();
    });

    // Reconnection event handlers
    this.connectionManager.on("connectionStateChanged", (state) => {
      console.log("Connection state changed:", state);
      this.emit("connectionStateChanged", state);

      // Note: Re-authentication is triggered in "socketOpened" event handler
    });

    this.connectionManager.on("reconnecting", (data) => {
      console.log(`Reconnecting... attempt ${data.attempt}/${data.maxRetries}`);
      this.emit("reconnecting", data);
    });

    this.connectionManager.on("maxRetriesReached", () => {
      console.log("Max reconnection retries reached");
      this.state = 'OFFLINE';
      this.emit("maxRetriesReached");
    });

    this.connectionManager.on("connectionClosed", (data) => {
      console.log("Connection closed:", data);

      // Clear ping interval
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }

      // Reset auth state for reconnection
      // This ensures that when socket reopens, auth will be triggered
      console.log("Resetting auth state to INITIAL for potential reconnection");
      this.authSessionManager.state = "INITIAL";
      this.state = "DISCONNECTED";

      // Generate new session ID for reconnection (prevents duplicate session error)
      if (this.hasEverConnected) {
        const newSessionId = generateRandomString(16) + Date.now();
        console.log("Generating new session ID for reconnection:", this.sessionId, "->", newSessionId);
        this.sessionId = newSessionId;
        this.authSessionId = newSessionId;

        // Update From-Path with new session ID
        this.fromPath = new MsrpUri("", this.host, this.port, newSessionId, "ws");

        console.log("Updated session ID and From-Path for reconnection");
      }
    });

    this.on("startAuth", () => {
      console.log("in start auth");
      this.onConnectionEstablished();
    });

    this.on("socketOpened", () => {
      console.log("socketOpened event received");

      // Always emit canStartAuth for initial login (LoginPage waits for this)
      this.emit("canStartAuth");

      // If this is a reconnection (not initial login), automatically trigger auth
      // Add 3-second delay before starting authentication
      if (this.hasEverConnected) {
        console.log("Reconnection detected - waiting 3 seconds before re-authentication");
        setTimeout(() => {
          console.log("3-second delay complete - triggering re-authentication");
          // Emit startAuth event (same as wrp.authorize() does in LoginPage)
          this.emit("startAuth");
        }, 3000);
      }
    });
    this.on("messageDelivered", (messageID, deliveredTime) => {
      console.log("message with id " + messageID + " is delivered at time: " + deliveredTime);
      this.emit("Delivered", messageID, deliveredTime);
    });

     this.on("messageSent", (messageID, sentTime) => {
      console.log("message with id " + messageID + " is sent at time: " + sentTime);
      this.emit("Sent", messageID, sentTime);
    });

    this.on("messageStored", (messageID, storedTime) => {
      console.log("Message stored (not delivered):", messageID, "at time:", storedTime);
      this.emit("Stored", messageID, storedTime);
    });

    // this.on("error", (err) => {
    //   console.error("endpoint connection error:", err);
    //   this.state = "DISCONNECTED"; // Update state appropriately
    //   // Emit error up to any listeners
    //   this.emit("connectionError", err);
    // });

    this.connectionManager.on("error", (err) => {
      console.error("endpoint code connection error:", err);
      this.state = "DISCONNECTED"; // Update state appropriately
      // Emit error up to any listeners

      clearInterval(this.pingInterval);

      this.emit("connectionError", err);
    });

    this.connectionManager.on("protocolSwitch", () => {
      console.log("protocol switch event received");
      this.authSessionManager.handleProtocolSwitch();
    });

    this.connectionManager.on("401", (rawMessage) => {
      console.log("401 Unauthorized event received");
      this.authSessionManager.handle401response(rawMessage);
    });

    this.connectionManager.on("403", (rawMessage) => {
      console.log("403 Forbidden event received");

      // Check if this is an "already logged in" error during reconnection
      const isAlreadyLoggedIn = rawMessage && (
        rawMessage.toLowerCase().includes('already') ||
        rawMessage.toLowerCase().includes('logged in') ||
        rawMessage.toLowerCase().includes('active session')
      );

      if (isAlreadyLoggedIn && this.hasEverConnected) {
        console.log("403 'Already logged in' detected during reconnection - treating as successful reconnection");

        // Reset auth failure count
        this.authFailureCount = 0;

        // Mark as authenticated
        this.connectionManager.connectionState = 'authenticated';
        this.connectionManager.emit('connectionStateChanged', 'authenticated');

        // Complete the auth process
        this.completeAuth();

        // Emit reconnected event to trigger post-reconnection logic
        this.emit("reconnected");

        return;
      }

      this.authFailureCount++;

      if (this.authFailureCount >= this.MAX_AUTH_FAILURES) {
        console.error("Max auth failures reached - session expired");
        this.authSessionManager.failAuth();
        this.emit("sessionExpired");

        // Redirect to login after delay
        toast.error("Session expired. Please log in again.");
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        console.log(`Auth failure ${this.authFailureCount}/${this.MAX_AUTH_FAILURES} - retrying...`);
        this.authSessionManager.failAuth();

        // Retry auth after delay
        setTimeout(() => {
          console.log("Retrying authentication...");
          this.authSessionManager.handleProtocolSwitch();
        }, 2000);
      }
    });

    this.connectionManager.on("auth_200", (rawMessage) => {
      console.log("200 auth arrived ");

      // Reset auth failure counter on success
      this.authFailureCount = 0;

      this.authSessionManager.handle200Auth(rawMessage);

      // Clear existing ping interval if any
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }

      // Send ping every 30 seconds
      this.pingInterval = setInterval(() => {
        console.log("Ping interval is 30 seconds");
        this.authSessionManager.handleSendPing();
      }, 30000); // 30 seconds

      // Emit reconnected event ONLY if this was a reconnection (not initial connection)
      if (this.hasEverConnected) {
        console.log("Reconnection detected - emitting 'reconnected' event");
        this.emit("reconnected");
      } else {
        console.log("Initial connection - not emitting 'reconnected' event");
      }

      // Mark that we've connected at least once
      this.hasEverConnected = true;
    });

    this.connectionManager.on("SEND", (message) => {
      console.log("on message event received");
      this.onMessage(message);
    });
  }

  openSocket(protocol) {
    this.connectionManager.openSocket(protocol, this.authHost, this.authPort);
  }

  connect(protocol) {
    this.state = "CONNECTING";
    console.log("endpoint state is --->" + this.state + "\n");
    this.connectionManager.openConnection(protocol, this.authHost, this.authPort);
  }

  onConnectionEstablished() {
    console.log("Connection established");
    this.state = "CONNECTED";
    console.log("state is --->" + this.state);
    console.log("authSessionManager state is --->" + this.authSessionManager.getState());

    if (this.authSessionManager.getState() === "INITIAL") {
      this.authSessionManager.handleProtocolSwitch();
    }
  }

  onAuthComplete() {
    this.state = "AUTHENTICATED";
    this.emit("auth_complete");
  }

  onAuthFailed() {
    this.state = "AUTH_FAILED";
    console.log("is in failed");
    this.emit("auth_failed");
  }

  sendFileMessage(file, contentId, contentDispo) {
    if (!file) {
      console.error("No file name provided");
      return;
    }

    console.log(`File size: ${file.size}, Last modified: ${file.lastModified}`);
    console.log(`File MIME type: ${file.type}`);

    const reader = new FileReader();

    reader.onerror = (err) => {
      console.error("Error reading file:", err);
    };

    reader.onload = () => {
      const fileContent = reader.result;

      console.log("File content:", fileContent);
      if (fileContent.length > 2048) {
        console.log("file packet needs chunking");

        const msgId = generateRandomString(16);
        const chmng = new ChunkManager();
        chmng.createChunks(fileContent);

        let currentChunk = chmng.getChunkHead();

        while (currentChunk !== null) {
          console.log("\n-----------------------------------------------------\n");

          const msg = this.manager.createEmptyMessage();
          this.manager.setTransactionId(msg, generateRandomString(16));
          this.manager.setMethod(msg, "SEND");
          this.manager.setToPath(msg, this.toPath.toString());
          this.manager.setFromPath(msg, this.fromPath.toString());
          this.manager.setMessageId(msg, msgId);
          this.manager.setBody(msg, currentChunk.msgContent);
          this.manager.calculateBodyLength(msg);
          this.manager.setByteRanges(msg, currentChunk.byteRangeStart, currentChunk.byteRangeEnd, currentChunk.byteRangeTotal);
          this.manager.setContentId(msg, contentId);
          this.manager.setContentDispo(msg, contentDispo);
          this.manager.setContentType(msg, "text/plain");
          this.manager.setContinuationFlag(msg, currentChunk.contFlag);

          console.log("this is the message to be sent" + this.manager.messageToString(msg));
          this.addPendingMessage(msg.transaction, msg);

          this.sendMessage("ws", this.manager.messageToString(msg), 1);

          currentChunk = currentChunk.next;

          console.log("\n-----------------------------------------------------\n");
        }
      } else {
        console.log("no need for chunking file packet , body size is : " + fileContent.length);
        const msg = this.manager.createEmptyMessage();
        this.manager.setTransactionId(msg, generateRandomString(16));
        this.manager.setMethod(msg, "SEND");
        this.manager.setToPath(msg, this.toPath.toString());
        this.manager.setFromPath(msg, this.fromPath.toString());
        this.manager.setMessageId(msg, generateRandomString(16));
        this.manager.setBody(msg, fileContent);
        this.manager.calculateBodyLength(msg);
        this.manager.setByteRanges(msg, 1, msg.bodyLength, msg.bodyLength);
        this.manager.setContentId(msg, contentId);
        this.manager.setContentDispo(msg, contentDispo);
        this.manager.setContentType(msg, "text/plain");
        this.manager.setContinuationFlag(msg, 1);

        console.log("this is the message to be sent" + this.manager.messageToString(msg));
        this.addPendingMessage(msg.transaction, msg);

        this.sendMessage("ws", this.manager.messageToString(msg), 1);
      }
    };

    reader.readAsBinaryString(file);
  }

  createAndSendMessage(body, type, headerMsgId = "") {
    if (body.length > 2048) {
      console.log("packet needs chunking");

      const msgId = headerMsgId || generateRandomString(16);
      const chmng = new ChunkManager();
      chmng.createChunks(body);

      let currentChunk = chmng.getChunkHead();

      while (currentChunk !== null) {
        console.log("\n-----------------------------------------------------\n");

        const msg = this.manager.createEmptyMessage();
        this.manager.setTransactionId(msg, generateRandomString(16));
        this.manager.setMethod(msg, "SEND");
        this.manager.setToPath(msg, this.toPath.toString());
        this.manager.setFromPath(msg, this.fromPath.toString());
        this.manager.setMessageId(msg, msgId);
        this.manager.setBody(msg, currentChunk.msgContent);
        this.manager.calculateBodyLength(msg);
        this.manager.setByteRanges(msg, currentChunk.byteRangeStart, currentChunk.byteRangeEnd, currentChunk.byteRangeTotal);
        this.manager.setContentType(msg, type);
        this.manager.setContinuationFlag(msg, currentChunk.contFlag);

        console.log("this is the message to be sent" + this.manager.messageToString(msg));
        this.addPendingMessage(msg.transaction, msg);

        this.sendMessage("ws", this.manager.messageToString(msg), 1);

        currentChunk = currentChunk.next;

        console.log("\n-----------------------------------------------------\n");
      }
    } else {
      console.log("header msg id is : " + headerMsgId);
      const msgId = headerMsgId || generateRandomString(16);

      console.log("message id is =", msgId);
      console.log("no need for chunking body size is : " + body.length);
      const msg = this.manager.createEmptyMessage();
      this.manager.setTransactionId(msg, generateRandomString(16));
      this.manager.setMethod(msg, "SEND");
      this.manager.setToPath(msg, this.toPath.toString());
      this.manager.setFromPath(msg, this.fromPath.toString());
      this.manager.setMessageId(msg, msgId);
      this.manager.setBody(msg, body);
      this.manager.calculateBodyLength(msg);
      this.manager.setByteRanges(msg, 1, msg.bodyLength, msg.bodyLength);
      this.manager.setContentType(msg, type);
      this.manager.setContinuationFlag(msg, "$");

      console.log("this is the message to be sent" + this.manager.messageToString(msg));
      this.addPendingMessage(msg.transaction, msg);
      this.sendMessage("ws", this.manager.messageToString(msg), 1);
    }
  }

  sendMessage(protocol, message, wsTranslate) {
    console.log("endPointManager :: trying to send the message, message length is =  " + message.length);

    //if(wsTranslate)
    //{
    //const msg = new WebSocketMessage(WEBSOCKET_OPCODE.DATA_TEXT, true, true, Buffer.from(message));
    //message = msg.build();
    //}
    this.connectionManager.sendMessage(protocol, message);
  }

  extractBoundary(text) {
    const lines = text.split(/\r?\n/);
    for (let line of lines) {
      if (line.startsWith("--")) {
        const boundary = line.slice(2).trim();
        if (boundary) {
          return boundary;
        }
      }
    }
    return null;
  }

  async onMessage(message) {
    var is_msg_to = false;
    var is_Edit = false;
    var is_Reply=false;
    var fromName = null;
    var toName = null;
    var chType = null;
    var currentMessageId=null;
    var repliedMessageId=null;

    console.log("=== INCOMING WEBSOCKET MESSAGE ===");
    console.log("Full message object:", JSON.stringify(message, null, 2));
    console.log("Message details:");
    console.log("  - Transaction ID:", message.transaction);
    console.log("  - Method:", message.method);
    console.log("  - Content Type:", message.cmn?.contentType);
    console.log("  - Continuation Flag:", message.contFlag);
    console.log("  - Message ID:", message.cmn?.messageId);
    console.log("  - From Path:", message.cmn?.fromPath);
    console.log("  - To Path:", message.cmn?.toPath);
    console.log("  - Body Length:", message.bodyLength);
    console.log("  - Message Content:", message.msgContent);
    console.log("  - Byte Range:", message.cmn?.byteRange);
    console.log("==================================");

    console.log("in on message", message);
    console.log("an MSRP message arrived with content type : " + message.cmn.contentType + " with flag :" + message.contFlag);

    var content_type = message.cmn.contentType;

    if (message.contFlag == "+") {
      console.log("we received a chunk from a bigger message ");

      if (!this.isWaiting) {
        this.isWaiting = true;
      }

      if (!this.chunkManager) {
        this.chunkManager = new ChunkManager();
      }

      this.chunkManager.addChunk(message);
    } else {
      if (this.isWaiting) {
        console.log("waiting for last chunk ");
        this.chunkManager.addChunk(message);

        const cmplt = this.chunkManager.getAllContent();
        message.msgContent = cmplt;
        this.chunkManager = null;
        this.isWaiting = false;
      }

      if (content_type == "text/plain") {
        console.log("we have a simple text message the content is : " + message.msgContent + "\n");
        this.emit("onMsg", message.msgContent.toString(), "", "", "");
      } else if (content_type == "application/rechat.cntl+json") {
        console.log("we have a control message \n");
        const parser = new ControlMessageParser();
        const parsedMessage = parser.parseDataForControlMessage(message.msgContent, message.msgContent.length);

        console.log("paredMesg", parsedMessage);
        if (parsedMessage) {
          // Check for media-comm and media-comm-sched requests/responses and dispatch window events for UI layers
          try {
            const parserObj = parsedMessage.parserObj || {};
            const parsedJobStr = parsedMessage.job && parsedMessage.job.str ? parsedMessage.job.str : parsedMessage.job;
            const isMediaComm = (typeof parserObj.detail === "string" && parserObj.detail.toLowerCase() === "media-comm") ||
              (parsedMessage.job && parsedMessage.job.str && parsedMessage.job.str === "media-comm") ||
              (parserObj.job && parserObj.job === "media-comm");
            const isMediaCommSchedule = (typeof parserObj.detail === "string" && parserObj.detail.toLowerCase() === "media-comm-sched") ||
              parsedJobStr === "media-comm-sched" ||
              (parserObj.job && parserObj.job === "media-comm-sched");

            if (isMediaComm || isMediaCommSchedule) {
              const messageType = parsedMessage.type || parserObj.type;
              const messageParams = parsedMessage.cm || parsedMessage.parameters || parserObj.parameters || null;
              
              const isRequest = messageType === CmTypes.CMTYPE_REQUEST || messageType === "request" || messageType === 1;
              
              if (isRequest) {
                const params = messageParams || parserObj.parameters || {};
                const fromName = params["from-name"] || params.fromName || "";
                const targetName = params["target-name"] || params.targetName || "";
                const commType = params["comm-type"] || params.commType || "audio";
                const mediaDesc = params["media-desc"] || params.mediaDesc || {};
                const schedTime = params["sched-time"] || params.schedTime || null;
                const duration = params.duration || 0.5;
                const callId = parsedMessage.id || parserObj.id || "";

                console.log(`Incoming ${isMediaCommSchedule ? "media-comm-sched" : "media-comm"} REQUEST received:`, {
                  id: callId,
                  fromName,
                  targetName,
                  commType,
                  mediaDesc,
                  schedTime,
                  duration,
                  rawParserObj: parserObj,
                  fullParsedMessage: parsedMessage
                });

                if (typeof window !== "undefined" && window.dispatchEvent) {
                  const eventName = isMediaCommSchedule ? "media-comm-sched-request" : "media-comm-request";
                  window.dispatchEvent(new CustomEvent(eventName, { 
                    detail: { 
                      id: callId, 
                      fromName, 
                      targetName, 
                      commType, 
                      mediaDesc,
                      schedTime,
                      duration,
                      rawMessage: parsedMessage
                    } 
                  }));
                }

                this.emit("ReqCmsg", parsedMessage);
                return;
              } else {
                // Response to our call
                const resultCode = parsedMessage["result-code"] || (messageParams && messageParams.result_code) || parsedMessage.result_code || (parserObj["result-code"]);
                const resultText = parsedMessage["result-text"] || (messageParams && messageParams.result_text) || parsedMessage.result_text || (parserObj["result-text"]);
                let methodDetected = null;
                try {
                  const params = parserObj.parameters || parserObj.parms || parserObj.parameters || (parsedMessage.cm && parsedMessage.cm.parms) || null;
                  if (params) {
                    if (typeof params["comm-method"] === "string") methodDetected = params["comm-method"];
                    else if (params["media-desc"] && typeof params["media-desc"]["comm-method"] === "string") methodDetected = params["media-desc"]["comm-method"];
                    else if (params["media-desc"] && typeof params["media-desc"] === "string") methodDetected = params["media-desc"];
                  }
                } catch (e) {
                }
                let raw = "";
                try { raw = JSON.stringify(parserObj); } catch (e) { raw = String(parserObj); }
                const isMediasoup = /mediasoup/i.test(raw) || (methodDetected && /mediasoup/i.test(methodDetected));

                console.log(`Media-comm${isMediaCommSchedule ? " schedule" : ""} RESPONSE received:`, {
                  id: parsedMessage.id,
                  resultCode,
                  resultText,
                  methodDetected,
                  isMediasoup,
                  rawParserObj: parserObj,
                });

                if (typeof window !== "undefined" && window.dispatchEvent) {
                  const eventName = isMediaCommSchedule ? "media-comm-sched-response" : "media-comm-response";
                  window.dispatchEvent(new CustomEvent(eventName, { detail: { id: parsedMessage.id, resultCode, resultText, methodDetected, isMediasoup } }));
                }

                // Stop further handling for response control message here
                return;
              }
            }
          } catch (e) {
            // Non-fatal - continue normal flow
            console.error('media-comm handling error', e);
          }
          switch (parsedMessage.type) {
            case CmTypes.CMTYPE_REQUEST:
              this.emit("ReqCmsg", parsedMessage);
              break;
            case CmTypes.CMTYPE_RESPONSE:
              this.emit("RespCmsg", parsedMessage);
              break;
            case CmTypes.CMTYPE_NOTIFY:
              this.emit("NotfCmsg", parsedMessage);
              break;
          }
        } else {
          console.error("Failed to parse the MSRP message.");
        }
      } else if (content_type.startsWith("multipart/related") && content_type.includes("boundary")) {
        console.log("we have a multipart message \n");
        const parser = new MultipartParser();
        const boundary = this.extractBoundary(this.manager.messageToString(message));
        console.log("boundary is : " + boundary);
        const messageId = message.cmn.messageId;
        console.log("message id is : " + messageId);

        const parsedLength = parser.parse(boundary, Buffer.from(this.manager.messageToString(message)));

        console.log("Parsed Length:", parsedLength);
        console.log("Number of parsed parts:", parser.bodies.length);

        for (let i = 0; i < parser.bodies.length; i++) {
          const abdy = parser.bodies[i];
          console.log(`body[${i}] header count: ${abdy.headerCount}`);
          console.log(`body[${i}] content: ${abdy.body.toString()}`);
          console.log("----------------------------------------------------------");

          for (let j = 0; j < abdy.headers.length; j++) {
            const hdr_field_str = abdy.headers[j].field;
            const hdr_value_str = abdy.headers[j].value;

            console.log(`header[${j}] field: ${hdr_field_str}, value: ${hdr_value_str}`);

            if (hdr_value_str === "application/rechat.cntl+json") {
              console.log("There is a control message part");
              const cmsgParser = new ControlMessageParser();
              const parsedCmsg = cmsgParser.parseDataForControlMessage(abdy.body, abdy.body.length);
              console.log("this is adby body: "+abdy.body);
              console.log("this is parsed cmsg: "+parsedCmsg);
              console.log("this is adby body parms: "+JSON.stringify(parsedCmsg.cm,null,2));
              if (parsedCmsg) {
                parsedCmsg.headerMsgId = messageId;
              }



              if (parsedCmsg) {

                console.log("parsed message job is :" + parsedCmsg.job);
                console.log("parsed message job string: "+JSON.stringify(parsedCmsg.job, null, 2));
                if (parsedCmsg.job === RequestJobs.REQJOB_MESSAGE_TO) {
                  const messageParams = parsedCmsg.cm;
                  fromName = messageParams.fromName;
                  toName = messageParams.toName;
                  chType = messageParams.messageToType;
                  is_msg_to = true;
                }else if (JSON.stringify(parsedCmsg.job).includes("edit-message")) {
                  const messageParams = parsedCmsg.cm;
                  currentMessageId=messageParams.currentMessageId;
                  fromName = messageParams.fromName;
                  toName = messageParams.toName;
                  chType = messageParams.messageToType;
                  console.log("Edit Message received for message ID:", parsedCmsg.currentMessageId);
                  is_Edit=true;
                }else if(JSON.stringify(parsedCmsg.job).includes("reply-to")){
                  console.log("reply message job is : "+JSON.stringify(parsedCmsg.job));
                  const messageParams = parsedCmsg.cm;
                  console.log("reply message params: "+JSON.stringify(messageParams,null,2));
                 repliedMessageId=messageParams.repliedMessageId;
                  console.log("replied message id is : "+repliedMessageId);
                  fromName = messageParams.fromName;
                  toName = messageParams.toName;
                  console.log("reply Message received for message ID:", repliedMessageId);
                  console.log("from",fromName," to",toName);
                  is_Reply=true;

                }
              }
            } else if (hdr_value_str === "text/plain") {
              if (is_msg_to) {
                console.log("there is a urgent message:",chType);
                if(chType=="urgent:a2a"){
                  console.log("there is a urgent message:",chType);
                  this.emit("onUrgentMsg", abdy.body.toString(), messageId, fromName, toName, chType);
                }
                console.log("There is a text message in a message to multipart");
                this.emit("onMsg", abdy.body.toString(), messageId, fromName, toName, chType);
              }else if(is_Edit){
                console.log("There is a text message in a edit message multipart");
                this.emit("onEdit", abdy.body.toString(), currentMessageId,fromName,toName,chType);
              }else if(is_Reply){
                console.log("There is a text message in a reply message multipart");
                this.emit("onReply", abdy.body.toString(), messageId,repliedMessageId,fromName,toName,chType);
              }
            }
          }
        }
      }else if (content_type.startsWith("multipart/relatedh;boundary")){
         console.log("we have a multipart message history");
          const boundary = this.extractBoundary(this.manager.messageToString(message));
                  console.log("boundary is : " + boundary);
            const result = await historyParser(this.manager.messageToString(message),boundary,this.username+"@"+this.companyDomain);
            console.log("Parsed messages:", JSON.stringify(result, null, 2));
            console.log("Number of parsed messages:", result);

            const pageInfo = result?.pageInfo;
            console.log("in here page info isss=", result?.pageInfo);

            const { pageNumber, totalPageNumber } = pageInfo || {};
            this.emit("onMsgHistory", result, pageNumber, totalPageNumber);
            // result.forEach(result => {
            //   this.emit("onMsgHistory", result.text, result.messageId, result.isOwner,result.sender,result.receiver,result.role, result.sentTime, result.deliveredTime, result.seenTimes, result.editTime, result.deletedTime, result.repliedMessageId,  result.messageType,  result.fileData, pageInfo?.pageNumber, pageInfo?.hasMore);
            // });
            
         
         }
    }
  }


  addPendingMessage(transactionId, message) {
    if (transactionId && message) {
      this.pendingMessages[transactionId] = message;
      console.log(`Message with transaction ID ${transactionId} added to pending messages.`);

      const timer = setTimeout(() => {
        console.log(`Message with transaction ID ${transactionId} has expired.`);
        this.removePendingMessage(transactionId);
      }, 40000);

      this.messageTimers[transactionId] = timer;
    } else {
      console.error("Invalid transaction ID or message.");
    }
  }

  removePendingMessage(transactionId) {
    if (this.pendingMessages[transactionId]) {
      delete this.pendingMessages[transactionId];
      console.log(`Message with transaction ID ${transactionId} removed from pending messages.`);

      if (this.messageTimers[transactionId]) {
        clearTimeout(this.messageTimers[transactionId]);
        delete this.messageTimers[transactionId];
      }
    } else {
      console.warn(`No pending message found with transaction ID ${transactionId}.`);
    }
  }

  getPendingMessage(transactionId) {
    return this.pendingMessages[transactionId];
  }

  closeConnection(protocol) {
    this.connectionManager.closeConnection(protocol);
    this.state = "DISCONNECTED";
    this.pendingMessages = {};
  }

  getState() {
    return this.state;
  }

  sendResponseToRequest(request, response_code, response_text) {
    console.log("Sending response to request...");
    const resp = this.manager.createEmptyMessage();
    this.manager.setTransactionId(resp, request.transaction.toString());
    this.manager.setResponseCode(resp, response_code);
    this.manager.setResponseText(resp, response_text);
    this.manager.setToPath(resp, request.cmn.fromPath.toString());
    this.manager.setFromPath(resp, request.cmn.toPath.toString());
    this.manager.setContinuationFlag(resp, "$");
    console.log("this is the response message to be sent:\n" + this.manager.responseMessageToString(resp));
    //this.addPendingMessage(resp.transaction , resp);
    this.sendMessage("ws", this.manager.responseMessageToString(resp), 1);
  }
}

export default Endpoint;
